import type { StyleSystemProps } from '@withjoy/joykit';
import { Box, ButtonV2, Flex, InputV1, SpacingStack, styled } from '@withjoy/joykit';
import type { ButtonOverrides } from '@withjoy/joykit/components/ButtonV2/Button.types';
import { pxToRem } from '@withjoy/joykit/theme';

export const StyledFormBody = styled(SpacingStack)``;

export const StyledFormInputFieldContainer = styled(Flex)`
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus-visible {
    outline: none !important;
  }

  * {
    &:focus-visible {
      outline: none !important;
    }
  }
`;

// font-size set to 16 because on Safari mobile fonts smaller than this trigger the accessibility zoom which breaks the layout
export const StyledInputNumber = styled(InputV1)`
  -moz-appearance: textfield;
  font-size: 16px;
  border-width: 1px;
  border-style: solid;
  border-radius: ${props => props.theme.radii.field};
  border-color: ${props => props.theme.colors.inputBorder};
`;

export const StyledQuantityToggleContainer = styled(Flex)`
  -moz-appearance: textfield;
  font-size: 16px;
  border-width: 2px;
  border-style: solid;
  border-radius: 26px;
  border-color: #d9d9d9;
  flex-direction: row;
  & div {
    border-style: none;
  }
  & input {
    text-align: center;
  }
`;

export const StyledForm = styled(Box)`
  width: 100%;
`;

export const StyledButtonWrapper = styled(SpacingStack)`
  flex: 1;
`;

export const StyledFlex = styled(Flex)`
  flex-direction: row;
  margin-bottom: 48px;
  margin-top: 0px;
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  @media screen and (min-width: 640px) {
    flex-flow: column;
    flex-direction: row;
    column-gap: 12px;
    width: 100%;
    margin-bottom: 48px;
    margin-top: 0px;
  }
  @media screen and (min-width: 768px) {
    flex-flow: column;
    flex-direction: row;
    column-gap: 12px;
    width: 328px;
    margin-bottom: 48px;
    margin-top: 48px;
  }
`;

export const StyledQuickAddFormFlex = styled(Flex)`
  flex-direction: row;
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const quickAddFormPlaceHolderStyles: StyleSystemProps = {
  flexDirection: { sm: 'row', sm2: 'row' },
  columnGap: { sm: 4, sm2: 4 },
  width: { sm: '100%', sm2: '328px' },
  marginTop: { sm2: 5, sm: 0 },
};

export const StyledButton = styled(ButtonV2)`
  /* // icon is misaligned without */
  > span > span {
    display: flex;
  }
  width: 100%;
`;

export const registryPrimaryButtonStyles: ButtonOverrides = {
  Root: {
    props: {
      color: 'white',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      backgroundSize: '200% 200%',
      backgroundColor: 'color(display-p3 0.337255 0 0.95)',
      backgroundImage: 'radial-gradient(circle at var(--mouse-x, 0%) var(--mouse-y, 50%), color(display-p3 0.286275 0.317647 0.937255) 0%, color(display-p3 0.337255 0 0.95) 100%)',
      backgroundPosition: '0% 50%',

      _hover: {
        color: 'white',
        backgroundSize: '100% 100%',
      },
      _active: {
        color: 'white',
        transform: 'scale(0.96)',
        transition: 'transform 0.2s ease-in-out',
      },
      _disabled: {
        backgroundImage: 'none',
      },
    },
  },
};

export const purchaseUnavailableButtonStyles: ButtonOverrides = {
  Root: {
    props: {
      ...registryPrimaryButtonStyles.Root?.props,
      _disabled: {
        backgroundImage: 'none',
        backgroundColor: 'mono3',
        borderColor: 'mono3',
        color: 'mono10',
      },
    },
  },
};

export const registrySecondaryButtonStyles: ButtonOverrides = {
  Root: {
    props: {
      color: 'mono14',
      backgroundColor: 'white',
      borderColor: 'mono14',
      _hover: {
        color: 'mono12',
        backgroundColor: 'mono3',
        borderColor: 'mono14',
      },
      _active: {
        color: 'mono14',
        backgroundColor: 'mono5',
        borderColor: 'mono14',
      },
      _disabled: {
        color: 'mono8',
        backgroundColor: 'white',
        borderColor: 'mono3',
      },
    },
  },
};

export const loadingBackground = {
  backgroundImage: 'radial-gradient(circle at var(--mouse-x, 0%) var(--mouse-y, 50%), color(display-p3 0.286275 0.317647 0.937255) 0%, color(display-p3 0.337255 0 0.95) 100%)',
};

export const addedToRegistryStyles = {
  backgroundColor: 'color(display-p3 0.0902 0.4353 0.4118)',
  backgroundImage: 'radial-gradient(circle at var(--mouse-x, 0%) var(--mouse-y, 50%), color(display-p3 0 0.5725 0.5373) 0%, color(display-p3 0.0902 0.4353 0.4118) 100%)',
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 50%',
  color: 'white',
  _hover: {
    color: 'white',
    backgroundSize: '100% 100%',
    backgroundColor: 'color(display-p3 0.337255 0 0.95)',
    backgroundImage: loadingBackground.backgroundImage,
  },
  _active: {
    color: 'white',
    transform: 'scale(0.96)',
    transition: 'transform 0.2s ease-in-out',
  },
};

export const stickyButtonWrapperStyles: StyleSystemProps = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  width: '100%',
  zIndex: 'popover',
  backgroundColor: 'white',
  padding: pxToRem(10),
  display: 'flex',
  justifyContent: 'center',
  boxShadow: '0px -8px 24px rgba(0, 0, 0, 0.15), 0px -4px 12px rgba(0, 0, 0, 0.1)',
};

export const styles = {
  registryPrimaryButton: registryPrimaryButtonStyles,
  registrySecondaryButton: registrySecondaryButtonStyles,
  purchaseUnavailableButton: purchaseUnavailableButtonStyles,
  quickAddFormPlaceHolder: quickAddFormPlaceHolderStyles,
  stickyButtonWrapper: stickyButtonWrapperStyles,
};
